import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Grid,
  Container,
  Divider,
  Alert
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  EventNote,
  Today
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, orange, green, red } from '@mui/material/colors';
import Header from '../Header';
import Footer from '../Footer';
import { fetchMemos, addMemo, updateMemo, deleteMemo as deleteMemoApi, fetchMonthMemoCounts } from '../../service/CalendarMemoService';

const theme = createTheme({
  palette: {
    primary: {
      main: blue[600],
    },
    secondary: {
      main: orange[500],
    },
  },
  typography: {
    fontFamily: '"Noto Sans JP", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

const CalendarMemoTool = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [memos, setMemos] = useState({});
  const [monthMemoCounts, setMonthMemoCounts] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMemo, setEditingMemo] = useState(null);
  const [memoText, setMemoText] = useState('');
  const [memoTitle, setMemoTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const today = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const monthNames = [
    '1月', '2月', '3月', '4月', '5月', '6月',
    '7月', '8月', '9月', '10月', '11月', '12月'
  ];

  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const firstDayWeekday = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  const calendarDays = [];
  
  // 前月の日付
  for (let i = firstDayWeekday - 1; i >= 0; i--) {
    const prevDate = new Date(currentYear, currentMonth, -i);
    calendarDays.push({
      date: prevDate,
      isCurrentMonth: false,
      day: prevDate.getDate()
    });
  }

  // 今月の日付  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentYear, currentMonth, day);
    calendarDays.push({
      date: date,
      isCurrentMonth: true,
      day: day
    });
  }

  // 次月の日付
  const remainingCells = 42 - calendarDays.length;
  for (let day = 1; day <= remainingCells; day++) {
    const nextDate = new Date(currentYear, currentMonth + 1, day);
    calendarDays.push({
      date: nextDate,
      isCurrentMonth: false,
      day: day
    });
  }

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentYear, currentMonth + direction, 1));
    setSelectedDate(null);
  };

  const selectDate = (date) => {
    setSelectedDate(date);
  };

  const openMemoDialog = (memo = null) => {
    if (memo) {
      setEditingMemo(memo.id);
      setMemoTitle(memo.title);
      setMemoText(memo.text);
    } else {
      setEditingMemo(null);
      setMemoTitle('');
      setMemoText('');
    }
    setOpenDialog(true);
  };

  const closeMemoDialog = () => {
    setOpenDialog(false);
    setEditingMemo(null);
    setMemoTitle('');
    setMemoText('');
  };

  const loadMemos = async (date) => {
    setLoading(true);
    setError('');
    try {
      const dateKey = formatDateKey(date);
      const res = await fetchMemos(dateKey);
      if (res.result) {
        setMemos(prev => ({ ...prev, [dateKey]: (res.memos || []).map(m => ({
          ...m,
          createDatetime: new Date(m.create_datetime)
        })) }));
      } else {
        setError('メモの取得に失敗しました');
      }
    } catch (e) {
      setError(e.message || 'メモの取得に失敗しました');
    }
    setLoading(false);
  };

  const loadMonthMemoCounts = async (year, month) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchMonthMemoCounts(year, month + 1); // JSのmonthは0始まり
      if (res.result) {
        setMonthMemoCounts(res.counts || {});
      } else {
        setError('月のメモ数取得に失敗しました');
      }
    } catch (e) {
      setError(e.message || '月のメモ数取得に失敗しました');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMonthMemoCounts(currentYear, currentMonth);
    // eslint-disable-next-line
  }, [currentYear, currentMonth]);

  useEffect(() => {
    if (selectedDate) {
      loadMemos(selectedDate);
    }
    // eslint-disable-next-line
  }, [selectedDate]);

  const saveMemo = async () => {
    if ((memoTitle.trim() || memoText.trim()) && selectedDate) {
      const dateKey = formatDateKey(selectedDate);
      setLoading(true);
      setError('');
      try {
        if (editingMemo) {
          // 編集
          const memo = (memos[dateKey] || []).find(m => m.id === editingMemo);
          await updateMemo(editingMemo, memoTitle.trim(), memoText.trim());
          setMemos(prev => ({
            ...prev,
            [dateKey]: prev[dateKey].map(m =>
              m.id === editingMemo ? { ...m, title: memoTitle.trim(), text: memoText.trim() } : m
            )
          }));
        } else {
          // 新規追加
          const res = await addMemo(dateKey, memoTitle.trim() || '無題', memoText.trim());
          if (res.result) {
            await loadMemos(selectedDate);
          }
        }
        await loadMonthMemoCounts(currentYear, currentMonth); // 追加・編集後に月のメモ数も更新
        closeMemoDialog();
      } catch (e) {
        setError(e.message || '保存に失敗しました');
      }
      setLoading(false);
    }
  };

  const deleteMemo = async (memoId) => {
    if (selectedDate) {
      setLoading(true);
      setError('');
      try {
        await deleteMemoApi(memoId);
        await loadMemos(selectedDate);
        await loadMonthMemoCounts(currentYear, currentMonth); // 削除後に月のメモ数も更新
      } catch (e) {
        setError(e.message || '削除に失敗しました');
      }
      setLoading(false);
    }
  };

  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const hasMemos = (date) => {
    const dateKey = formatDateKey(date);
    return (monthMemoCounts[dateKey] && monthMemoCounts[dateKey] > 0);
  };

  const getSelectedDateMemos = () => {
    if (!selectedDate) return [];
    const dateKey = formatDateKey(selectedDate);
    return memos[dateKey] || [];
  };

  const getMemoCount = (date) => {
    const dateKey = formatDateKey(date);
    return monthMemoCounts[dateKey] || 0;
  };

  return (
    <ThemeProvider theme={theme}>
    <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={4}>
            {/* カレンダー部分 */}
            <Box flex={1}>
              {/* ヘッダー */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <EventNote color="primary" fontSize="large" />
                  <Typography variant="h4" component="h1" fontWeight="bold">
                    カレンダーメモ
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <IconButton onClick={() => navigateMonth(-1)} size="large">
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="h5" component="h2" fontWeight="medium" minWidth="140px" textAlign="center">
                    {currentYear}年 {monthNames[currentMonth]}
                  </Typography>
                  <IconButton onClick={() => navigateMonth(1)} size="large">
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>

              {/* 曜日ヘッダー */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1} mb={1}>
                {weekDays.map((day, index) => (
                  <Box
                    key={day}
                    height={40}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={index === 0 ? red[50] : index === 6 ? blue[50] : 'transparent'}
                    borderRadius={1}
                  >
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="bold"
                      color={index === 0 ? red[600] : index === 6 ? blue[600] : 'text.secondary'}
                    >
                      {day}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* カレンダーグリッド */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
                {calendarDays.map((dayObj, index) => (
                  <Button
                    key={index}
                    onClick={() => selectDate(dayObj.date)}
                    variant={isSelected(dayObj.date) ? "contained" : "outlined"}
                    color={isToday(dayObj.date) ? "secondary" : "primary"}
                    sx={{
                      width: '100%',
                      height: 60,
                      minWidth: 0,
                      position: 'relative',
                      flexDirection: 'column',
                      opacity: dayObj.isCurrentMonth ? 1 : 0.5,
                      border: hasMemos(dayObj.date) ? `2px solid ${orange[400]}` : undefined,
                      '&:hover': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  >
                    <Typography variant="body2" fontWeight={isToday(dayObj.date) ? "bold" : "normal"}>
                      {dayObj.day}
                    </Typography>
                    {hasMemos(dayObj.date) && (
                      <Chip
                        label={getMemoCount(dayObj.date)}
                        size="small"
                        color="secondary"
                        sx={{ 
                          position: 'absolute',
                          bottom: 4,
                          right: 4,
                          minWidth: 20,
                          height: 16,
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* メモ部分 */}
            <Box width={{ lg: 400 }}>
              <Paper variant="outlined" sx={{ p: 3, height: 'fit-content', minHeight: 400 }}>
                {selectedDate ? (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Today color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                          {selectedDate.getFullYear()}年{selectedDate.getMonth() + 1}月{selectedDate.getDate()}日
                        </Typography>
                      </Box>
                      <Fab
                        size="small"
                        color="primary"
                        onClick={() => openMemoDialog()}
                        sx={{ boxShadow: 2 }}
                      >
                        <Add />
                      </Fab>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* メモ一覧 */}
                    <Box display="flex" flexDirection="column" gap={2}>
                      {getSelectedDateMemos().map(memo => (
                        <Card key={memo.id} variant="outlined" sx={{ borderLeft: `4px solid ${blue[500]}` }}>
                          <CardContent sx={{ pb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {memo.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                              {memo.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {memo.createDatetime.toLocaleString('ja-JP')}
                            </Typography>
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => openMemoDialog(memo)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => deleteMemo(memo.id)}
                            >
                              <Delete />
                            </IconButton>
                          </CardActions>
                        </Card>
                      ))}
                      
                      {getSelectedDateMemos().length === 0 && (
                        <Alert severity="info" sx={{ textAlign: 'center' }}>
                          この日にはメモがありません。<br />
                          右上の＋ボタンからメモを追加できます。
                        </Alert>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={300} textAlign="center">
                    <EventNote sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      日付を選択してください
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      カレンダーから日付をクリックすると<br />
                      メモの表示・編集ができます
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </Paper>

        {/* メモ追加・編集ダイアログ */}
        <Dialog 
          open={openDialog} 
          onClose={closeMemoDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              {editingMemo ? <Edit color="primary" /> : <Add color="primary" />}
              {editingMemo ? 'メモを編集' : 'メモを追加'}
            </Box>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="タイトル"
              fullWidth
              variant="outlined"
              value={memoTitle}
              onChange={(e) => setMemoTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="メモ内容"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={memoText}
              onChange={(e) => setMemoText(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={closeMemoDialog} startIcon={<Cancel />}>
              キャンセル
            </Button>
            <Button 
              onClick={saveMemo} 
              variant="contained" 
              startIcon={<Save />}
              disabled={!memoTitle.trim() && !memoText.trim()}
            >
              保存
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </ThemeProvider>
  );
};

export default CalendarMemoTool;